import React, { Fragment, useMemo, useState, useRef } from 'react'
import classNames from 'classnames'
import PaginationPage from './PaginationPage'

import style from './PaginationV2.module.css'

function PaginationV2({
  className,
  selectedPageNumber,
  totalPages,
  onPageChanged,
  maxPagesShown = 5,
  type,
  icons,
  testId,
  id
}) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const listBox = useRef()

  const minPageNumber = useMemo(() => {
    const idealNeighborCount = Math.floor(maxPagesShown / 2)
    if (selectedPageNumber > idealNeighborCount + 1) {
      if (selectedPageNumber + idealNeighborCount <= totalPages) {
        return selectedPageNumber - idealNeighborCount
      } else {
        if (totalPages >= maxPagesShown) {
          return totalPages - maxPagesShown + 1
        }
      }
    }
    return 1
  }, [selectedPageNumber, maxPagesShown, totalPages])
  const pageNumbers = useMemo(
    () =>
      Array.from(Array(Math.min(maxPagesShown, totalPages)).keys()).map(
        (n) => n + minPageNumber
      ),
    [totalPages, maxPagesShown, minPageNumber]
  )

  const isFirstPageShown = useMemo(() => pageNumbers[0] !== 1, [pageNumbers])

  const isLastPageShown = useMemo(
    () => pageNumbers[pageNumbers.length - 1] !== totalPages,
    [pageNumbers, totalPages]
  )

  const leftJumpPageNumber = useMemo(
    () => pageNumbers[0] > 2 && Math.max(1, selectedPageNumber - maxPagesShown),
    [pageNumbers, selectedPageNumber, maxPagesShown]
  )

  const rightJumpPageNumber = useMemo(
    () =>
      pageNumbers[pageNumbers.length - 1] < totalPages - 1 &&
      Math.min(totalPages, selectedPageNumber + maxPagesShown),
    [pageNumbers, selectedPageNumber, maxPagesShown, totalPages]
  )

  const handleKeyDown = (event, value) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault()
      const listItems = listBox.current.querySelectorAll('[role="option"]')
      if (listItems.length > 0) {
        let newIndex
        if (event.key === 'ArrowRight') {
          newIndex = Math.min(
            selectedIndex + 1,
            listItems.length - 1
          )
        } else {
          newIndex = Math.max(selectedIndex - 1, 0)
        }
        listItems[newIndex].focus()
        setSelectedIndex(newIndex)
      }
    } else if (event.key === 'Enter') {
      const focusedOption = document.activeElement
      if (focusedOption && focusedOption.getAttribute('role') === 'option') {
        const button = focusedOption.querySelector('button')
        if (button) {
          button.click()
        } else {
          setSelectedIndex(0)
          if(value){
            onPageChanged(value)
          }
        }
      }
    }
  }
  return (
    <div className={classNames(style.container, className)}>
      <ul
        className={classNames(
          style.list,
          type === 'primary' && style.primary_pagination
        )}
        data-testid={testId}
        role='listbox'
        id='listbox'
        aria-label='Select Pagination'
        aria-labelledby={id}
        tabIndex={0}
        ref={listBox}
        onKeyDown={handleKeyDown}
      >
        {type === 'primary' ? (
          <Fragment>
            {leftJumpPageNumber && (
              <PaginationPage
                pageNumber={leftJumpPageNumber}
                onPageChanged={onPageChanged}
                icon={icons && icons.left ? icons.left : ''}
                text='&laquo;'
                type={type}
                id='listbox'
                title={leftJumpPageNumber}
                ariaLabel={leftJumpPageNumber}
                tabIndex={0}
                handleKeyDown={handleKeyDown}
              />
            )}
            {isFirstPageShown && (
              <PaginationPage
                pageNumber={1}
                onPageChanged={onPageChanged}
                type={type}
                id='listbox'
                title={1}
                ariaLabel={1}
                tabIndex={0}
                handleKeyDown={handleKeyDown}
              />
            )}
          </Fragment>
        ) : (
          <Fragment>
            {isFirstPageShown && (
              <PaginationPage
                pageNumber={1}
                onPageChanged={onPageChanged}
                id='listbox'
                title={1}
                ariaLabel={1}
                tabIndex={0}
                handleKeyDown={handleKeyDown}
              />
            )}
            {leftJumpPageNumber && (
              <PaginationPage
                pageNumber={leftJumpPageNumber}
                onPageChanged={onPageChanged}
                icon={icons && icons.left ? icons.left : ''}
                text='&laquo;'
                id='listbox'
                title={leftJumpPageNumber}
                ariaLabel={leftJumpPageNumber}
                tabIndex={0}
                handleKeyDown={handleKeyDown}
              />
            )}
          </Fragment>
        )}
        {pageNumbers.map((pageNumber) => (
          <PaginationPage
            key={pageNumber}
            pageNumber={pageNumber}
            onPageChanged={onPageChanged}
            isSelected={pageNumber === selectedPageNumber}
            type={type}
            id='listbox'
            title={pageNumber}
            ariaLabel={`Page ${pageNumber}`}
            tabIndex={0}
            handleKeyDown={handleKeyDown}
          />
        ))}
        {type === 'primary' ? (
          <Fragment>
            {isLastPageShown && (
              <PaginationPage
                pageNumber={totalPages}
                onPageChanged={onPageChanged}
                type={type}
                id='listbox'
                title={totalPages}
                ariaLabel={totalPages}
                tabIndex={0}
                handleKeyDown={handleKeyDown}
              />
            )}
            {rightJumpPageNumber && (
              <PaginationPage
                pageNumber={rightJumpPageNumber}
                onPageChanged={onPageChanged}
                icon={icons && icons.right ? icons.right : ''}
                text='&raquo;'
                type={type}
                id='listbox'
                title={rightJumpPageNumber}
                ariaLabel={rightJumpPageNumber}
                tabIndex={0}
                handleKeyDown={handleKeyDown}
              />
            )}
          </Fragment>
        ) : (
          <Fragment>
            {rightJumpPageNumber && (
              <PaginationPage
                pageNumber={rightJumpPageNumber}
                onPageChanged={onPageChanged}
                icon={icons && icons.right ? icons.right : ''}
                text='&raquo;'
                id='listbox'
                title={rightJumpPageNumber}
                ariaLabel={rightJumpPageNumber}
                tabIndex={0}
                handleKeyDown={handleKeyDown}
              />
            )}
            {isLastPageShown && (
              <PaginationPage
                pageNumber={totalPages}
                onPageChanged={onPageChanged}
                id='listbox'
                title={totalPages}
                ariaLabel={totalPages}
                tabIndex={0}
                handleKeyDown={handleKeyDown}
              />
            )}
          </Fragment>
        )}
      </ul>
    </div>
  )
}

export default PaginationV2
